import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Volume2, VolumeX, Mic } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { streamChat } from "../lib/api";

const SUGGESTIONS = [
  "¿Quién fue el Duque de Lerma?",
  "¿Qué ver en una hora?",
  "Cuéntame la Plaza Ducal",
  "¿Dónde como bien?",
];

export const ChatGuide = ({ sessionId, monument, speak, stopSpeak, speaking, autoTts, pendingMessage, onConsumePending, onTranscript }) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text:
        "Bienvenido a Lerma. Soy Catalina, vuestra cronista en esta villa del Siglo de Oro. ¿Sobre qué monumento o historia deseáis que os hable?",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ttsOn, setTtsOn] = useState(autoTts ?? true);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (pendingMessage) {
      send(pendingMessage);
      onConsumePending?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMessage]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    // Refuerzo del desbloqueo de voz para Firefox (ver App.js): la respuesta
    // de Catalina llega async tras esperar al servidor, así que aseguramos
    // aquí también que hubo un speak() síncrono dentro de este clic/Enter.
    if (ttsOn && "speechSynthesis" in window) {
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(""));
    }
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    let acc = "";
    await streamChat(
      { sessionId, message: msg, monumentId: monument?.id },
      (delta) => {
        acc += delta;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: acc };
          return copy;
        });
      },
      () => {
        setBusy(false);
        if (ttsOn && acc) speak(acc);
      },
      (err) => {
        setBusy(false);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: err };
          return copy;
        });
      }
    );
  };

  return (
    <div className="flex flex-col h-screen pt-14 pb-40 px-4">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full flex items-center justify-center gold-glow" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
                        <span className="font-serif-display text-lg gold-text">C</span>
          </div>
          <div>
            <p className="font-serif-display text-xl leading-none" style={{ color: "#f3f4f6" }}>Catalina</p>
            <p className="text-[11px]" style={{ color: "#8c7326" }}>Cronista de Lerma · Siglo de Oro</p>
          </div>
        </div>
        <button
          data-testid="chat-tts-toggle"
          onClick={() => {
            if (ttsOn) stopSpeak();
            setTtsOn(!ttsOn);
          }}
          className="w-10 h-10 rounded-full glass flex items-center justify-center"
        >
          {ttsOn ? <Volume2 size={18} className="gold-text" /> : <VolumeX size={18} style={{ color: "#6b7280" }} />}
        </button>
      </div>

      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto thin-scroll space-y-3 px-1">
        {messages.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] px-4 py-3 rounded-2xl ${m.role === "user" ? "rounded-br-md" : "rounded-bl-md"}`}
              style={
                m.role === "user"
                  ? { background: "rgba(255,255,255,0.08)", color: "#f3f4f6" }
                  : { background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.15)" }
              }
            >
              {m.role === "assistant" ? (
                <p className="font-serif-display text-lg leading-snug" style={{ color: "#f0e6c8" }}>
                  {m.text || (busy && i === messages.length - 1 ? "…" : "")}
                </p>
              ) : (
                <p className="text-sm" style={{ color: "#f3f4f6" }}>{m.text}</p>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {messages.length <= 2 && (
        <div className="flex gap-2 overflow-x-auto thin-scroll py-3 px-1">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              data-testid="chat-suggestion"
              onClick={() => send(s)}
              className="whitespace-nowrap text-xs px-3.5 py-2 rounded-full glass"
              style={{ color: "#d4af37" }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[92%] max-w-md">
        <div className="glass rounded-full flex items-center gap-2 pl-5 pr-2 py-2">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Pregunta a Catalina…"
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: "#f3f4f6" }}
          />
          {onTranscript && <MicButton onTranscript={onTranscript} inline />}
          <button
            data-testid="chat-send-btn"
            onClick={() => send()}
            disabled={busy}
            className="w-10 h-10 rounded-full btn-gold flex items-center justify-center disabled:opacity-50"
          >
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
};
